import { Dispatch } from 'redux';
import { Services } from '../../types/services';

const GET_ALL_PIMS = 'GET_ALL_PIMS';

interface Pims {
  id?: string;
  name?: string;
  [key: string]: unknown;
}

interface PimsSettings {
  directory: string[];
  dbName: string[];
  dbPassword: string[];
  dbUser: string[];
  host: string[];
  odbcVersion: string[];
  server: string[];
  storageType: string[];
  tableExtensions: string[];
  tablesAll: string[];
  tablesSpecific: string[];
}

interface State {
  allPims: Pims[];
  pimsSettings: PimsSettings;
}

const initialState: State = {
  allPims: [],
  pimsSettings: {
    directory: ['AVIMARK'],
    dbName: [
      'ADVANTAGE',
      'CORNERSTONE',
      'IMPROMED',
      'VETBLUE',
      'VETTERSOFT',
      'VTECHPLATINUM',
      'WOOFWARE',
    ],
    dbPassword: [
      'ADVANTAGE',
      'CORNERSTONE',
      'DENTRIX',
      'IMPROMED',
      'VETBLUE',
      'VETTERSOFT',
      'VTECHPLATINUM',
      'WOOFWARE',
    ],
    dbUser: [
      'ADVANTAGE',
      'CORNERSTONE',
      'DENTRIX',
      'IMPROMED',
      'VETBLUE',
      'VETTERSOFT',
      'VTECHPLATINUM',
      'WOOFWARE',
    ],
    host: ['CORNERSTONE', 'DENTRIX'],
    odbcVersion: ['ADVANTAGE', 'CORNERSTONE', 'IMPROMED', 'WOOFWARE'],
    server: [
      'ADVANTAGE',
      'CORNERSTONE',
      'IMPROMED',
      'VETBLUE',
      'VETTERSOFT',
      'VTECHPLATINUM',
      'WOOFWARE',
    ],
    storageType: [
      'ADVANTAGE',
      'AVIMARK',
      'CORNERSTONE',
      'DVMAX',
      'DVMMANAGER',
      'EVETPRACTICE',
      'IMPROMED',
      'NEO',
      'VETBLUE',
      'VETTERSOFT',
      'VTECHPLATINUM',
      'WOOFWARE',
    ],
    tableExtensions: ['AVIMARK'],
    tablesAll: [
      'ADVANTAGE',
      'AVIMARK',
      'CORNERSTONE',
      'IMPROMED',
      'VETBLUE',
      'VETTERSOFT',
      'VTECHPLATINUM',
      'WOOFWARE',
    ],
    tablesSpecific: [
      'ADVANTAGE',
      'AVIMARK',
      'CORNERSTONE',
      'IMPROMED',
      'VETBLUE',
      'VETTERSOFT',
      'VTECHPLATINUM',
      'WOOFWARE',
    ],
  },
};

interface Action {
  type: string;
  payload?: unknown;
}

export const reducer = (state = initialState, action: Action): State => {
  switch (action.type) {
    case GET_ALL_PIMS:
      return { ...state, allPims: action.payload as Pims[] };
    default:
      return state;
  }
};

export const actions = (services: Services) => {
  const axiosCall = services.axios.Axios();

  const setAllPims = (allPims: Pims[]) => ({ type: GET_ALL_PIMS, payload: allPims });

  const getAllPims = () => async () => axiosCall.get('pims');

  const fetchAllPims = () => {
    return async (dispatch: Dispatch) => {
      const response = await axiosCall.get('pims');
      const allPims = (response as unknown as { data: Pims[] }).data;
      dispatch(setAllPims(allPims));
    };
  };

  const fetchPims = (pimsId: string) => {
    return async () => {
      try {
        const response = await axiosCall.get('pims/' + pimsId);
        return (response as unknown as { data: Pims }).data;
      } catch (error: unknown) {
        console.error((error as Error).message);
      }
    };
  };

  const togglePimsSupportsConnectivity = (pimsId: string) => {
    return async () => {
      try {
        await axiosCall.patch('pims/' + pimsId + '/connectivity');
      } catch (error: unknown) {
        console.error((error as Error).message);
      }
    };
  };

  const saveNewPims = (payload: Record<string, unknown> = {}) => {
    return async () => {
      try {
        const response = await axiosCall.post('pims/new', payload);
        return (response as unknown as { data: Pims }).data;
      } catch (error: unknown) {
        console.error((error as Error).message);
      }
    };
  };

  const createPimsConfig = (payload: Record<string, unknown> = {}) => {
    return async () => {
      try {
        await axiosCall.post('pims/config', payload);
      } catch (error: unknown) {
        console.error((error as Error).message);
      }
    };
  };

  const updatePimsConfig = (payload: Record<string, unknown> = {}) => {
    return async () => {
      try {
        await axiosCall.patch('pims/config', payload);
      } catch (error: unknown) {
        console.error((error as Error).message);
      }
    };
  };

  const updatePimsAppRelation = (payload: Record<string, unknown> = {}) => {
    return async () => {
      try {
        await axiosCall.patch('pims/pimsAppRelation', payload);
      } catch (error: unknown) {
        console.error((error as Error).message);
      }
    };
  };

  return {
    createPimsConfig,
    fetchAllPims,
    fetchPims,
    getAllPims,
    saveNewPims,
    togglePimsSupportsConnectivity,
    updatePimsAppRelation,
    updatePimsConfig,
  };
};

